(() => {
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let reduceMotion = motionPreference.matches;
  const cleanupRegistry = new Map();
  const registerCleanup = (root, dispose) => {
    if (!cleanupRegistry.has(root)) cleanupRegistry.set(root, []);
    cleanupRegistry.get(root).push(dispose);
  };
  const cleanup = scope => {
    for (const [root, disposers] of cleanupRegistry) {
      if (root === scope || scope.contains(root) || !root.isConnected) {
        disposers.forEach(dispose => dispose());
        delete root.dataset.chandiReady;
        cleanupRegistry.delete(root);
      }
    }
  };

  const initTabs = root => {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length || !panels.length) return;

    const select = tab => {
      tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        select(tabs[next]);
        tabs[next].focus();
      });
    });
  };

  const initBenefits = track => {
    if (track.dataset.chandiReady === 'true') return;
    track.dataset.chandiReady = 'true';
    const cards = [...track.children];
    const controls = track.closest('section')?.querySelector('[data-chandi-carousel-pause]');
    if (cards.length < 2) { if (controls) controls.hidden = true; return; }
    const controller = new AbortController();
    const bind = (type, callback, options = {}) => track.addEventListener(type, callback, { ...options, signal: controller.signal });
    let loopWidth = 0;
    let userPaused = false;
    let interacting = false;
    let focused = false;
    let editorSelected = false;
    let resumeTimer;
    let drag;
    let frame;
    let centerFrame;
    let previous = performance.now();
    let pendingPixels = 0;
    let glowTimer;
    let glowEnd;
    const isPaused = () => userPaused || interacting || focused || editorSelected || reduceMotion;
    const cloneSet = () => cards.forEach(card => {
      const clone = card.cloneNode(true);
      clone.dataset.chandiClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('video').forEach(video => { video.controls = false; });
      clone.removeAttribute('role');
      [clone, ...clone.querySelectorAll('*')].forEach(node => {
        node.removeAttribute('id');
        [...node.attributes].filter(attribute => attribute.name.startsWith('data-shopify-')).forEach(attribute => node.removeAttribute(attribute.name));
      });
      track.appendChild(clone);
    });
    const build = () => {
      track.querySelectorAll('[data-chandi-clone]').forEach(clone => clone.remove());
      cloneSet();
      loopWidth = track.children[cards.length].offsetLeft - cards[0].offsetLeft;
      if (loopWidth <= 0) return;
      for (let repeats = 0; track.scrollWidth - track.clientWidth < loopWidth * 2 + 4 && repeats < 40; repeats++) cloneSet();
      track.scrollLeft = loopWidth;
    };
    const updateCenter = () => {
      if (centerFrame) return;
      centerFrame = requestAnimationFrame(() => {
        centerFrame = 0;
        if (!track.isConnected) return;
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        let closest;
        let distance = Infinity;
        [...track.children].forEach(card => {
          const box = card.getBoundingClientRect();
          const delta = Math.abs(box.left + box.width / 2 - center);
          if (delta < distance) { closest = card; distance = delta; }
        });
        [...track.children].forEach(card => card.classList.toggle('is-centered', card === closest));
      });
    };
    const pauseInteraction = () => { interacting = true; clearTimeout(resumeTimer); };
    const resumeSoon = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { interacting = false; }, 2400);
    };
    const updatePause = () => {
      if (!controls) return;
      controls.setAttribute('aria-pressed', String(userPaused));
      controls.textContent = userPaused ? 'Beweging hervatten' : 'Beweging pauzeren';
      track.querySelectorAll('video').forEach(video => {
        if (userPaused || reduceMotion) video.pause();
        else video.play()?.catch(() => {});
      });
    };
    controls?.addEventListener('click', () => { userPaused = !userPaused; updatePause(); }, { signal: controller.signal });
    bind('scroll', () => {
      if (!editorSelected && loopWidth > 0) {
        if (track.scrollLeft >= loopWidth * 2) track.scrollLeft -= loopWidth;
        else if (track.scrollLeft < loopWidth) track.scrollLeft += loopWidth;
      }
      updateCenter();
    }, { passive: true });
    bind('pointerdown', event => {
      pauseInteraction();
      if (event.pointerType === 'mouse') {
        drag = { x: event.clientX, left: track.scrollLeft };
        track.setPointerCapture?.(event.pointerId);
      }
    });
    bind('pointermove', event => {
      if (!drag) return;
      track.scrollLeft = drag.left - (event.clientX - drag.x);
      if (loopWidth > 0) {
        if (track.scrollLeft >= loopWidth * 2) { track.scrollLeft -= loopWidth; drag.left -= loopWidth; }
        else if (track.scrollLeft < loopWidth) { track.scrollLeft += loopWidth; drag.left += loopWidth; }
      }
    });
    const endDrag = () => { drag = null; resumeSoon(); };
    bind('pointerup', endDrag);
    bind('pointercancel', endDrag);
    bind('touchstart', pauseInteraction, { passive: true });
    bind('touchend', resumeSoon, { passive: true });
    bind('touchcancel', resumeSoon, { passive: true });
    bind('wheel', () => { pauseInteraction(); resumeSoon(); }, { passive: true });
    bind('focusin', () => { focused = true; });
    bind('focusout', event => { focused = track.contains(event.relatedTarget); });
    bind('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      track.scrollLeft += (event.key === 'ArrowRight' ? 1 : -1) * (cards[0].getBoundingClientRect().width + 18);
      pauseInteraction();
      resumeSoon();
    });
    const selectBlock = event => {
      if (!track.contains(event.target)) return;
      editorSelected = true;
      const card = event.target.closest('[data-shopify-editor-block]') || event.target;
      track.scrollLeft = card.offsetLeft - cards[0].offsetLeft;
    };
    document.addEventListener('shopify:block:select', selectBlock, { signal: controller.signal });
    document.addEventListener('shopify:block:deselect', () => {
      if (editorSelected) { editorSelected = false; track.scrollLeft = loopWidth; }
    }, { signal: controller.signal });
    let previousWidth = track.clientWidth;
    const resize = () => {
      if (!track.isConnected || track.clientWidth === previousWidth) return;
      previousWidth = track.clientWidth;
      build();
      updateCenter();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(track);
    const randomGlow = () => {
      glowTimer = setTimeout(() => {
        if (!track.isConnected) return;
        if (!isPaused()) {
          const parent = track.getBoundingClientRect();
          const visible = [...track.children].filter(card => {
            const box = card.getBoundingClientRect();
            return !card.classList.contains('chandi-benefit--glow') && box.right > parent.left && box.left < parent.right;
          });
          const target = visible[Math.floor(Math.random() * visible.length)];
          if (target) { target.classList.add('is-random-glow'); glowEnd = setTimeout(() => target.classList.remove('is-random-glow'), 1900); }
        }
        randomGlow();
      }, 4800 + Math.random() * 4200);
    };
    const speed = Number(track.dataset.chandiSpeed) || .042;
    const animate = now => {
      if (!track.isConnected) return;
      const elapsed = Math.min(32, now - previous);
      previous = now;
      if (!isPaused() && loopWidth > 0) {
        pendingPixels += elapsed * speed;
        const pixels = Math.floor(pendingPixels);
        if (pixels > 0) { track.scrollLeft += pixels; pendingPixels -= pixels; }
      }
      frame = requestAnimationFrame(animate);
    };
    build();
    updateCenter();
    updatePause();
    if (!track.hasAttribute('data-chandi-image-carousel')) randomGlow();
    frame = requestAnimationFrame(animate);
    registerCleanup(track, () => {
      controller.abort();
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
      cancelAnimationFrame(centerFrame);
      clearTimeout(resumeTimer);
      clearTimeout(glowTimer);
      clearTimeout(glowEnd);
      track.querySelectorAll('[data-chandi-clone]').forEach(clone => clone.remove());
      delete track.dataset.chandiReady;
    });
  };

  const initAnnouncement = root => {
    if (root.dataset.chandiReady === 'true') return;
    root.dataset.chandiReady = 'true';
    const controller = new AbortController();
    const bind = (node, type, callback, options = {}) => node?.addEventListener(type, callback, { ...options, signal: controller.signal });
    const syncHeight = () => document.documentElement.style.setProperty('--chandi-announcement-height', `${root.isConnected ? root.getBoundingClientRect().height : 0}px`);
    const observer = new ResizeObserver(syncHeight);
    observer.observe(root);
    const items = [...root.querySelectorAll('[data-announcement-item]')];
    const dots = [...root.querySelectorAll('[data-announcement-dot]')];
    let current = 0;
    let timer;
    let busy = false;
    let startX = 0;
    const stop = () => clearInterval(timer);
    const expose = () => {
      items.forEach((item, index) => {
        item.inert = index !== current;
        item.setAttribute('aria-hidden', String(index !== current));
        item.classList.toggle('is-active', index === current);
      });
      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === current));
    };
    const show = direction => {
      if (busy || !root.isConnected || root.hidden || items.length < 2) return;
      busy = true;
      const old = items[current];
      current = (current + direction + items.length) % items.length;
      if (!reduceMotion) {
        old.classList.add('is-leaving');
        setTimeout(() => old.classList.remove('is-leaving'), 650);
      }
      expose();
      setTimeout(() => { busy = false; }, reduceMotion ? 0 : 650);
    };
    const start = () => {
      stop();
      if (!reduceMotion && items.length > 1 && root.isConnected && !root.hidden && !root.contains(document.activeElement) && !root.matches(':hover')) timer = setInterval(() => show(1), Number(root.dataset.speed) || 5000);
    };
    bind(root.querySelector('[data-announcement-close]'), 'click', () => {
      stop();
      root.inert = true;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      syncHeight();
      document.querySelector('[data-menu-open]')?.focus();
    });
    bind(root.querySelector('[data-announcement-prev]'), 'click', () => { show(-1); start(); });
    bind(root.querySelector('[data-announcement-next]'), 'click', () => { show(1); start(); });
    bind(root, 'touchstart', event => { stop(); startX = event.changedTouches[0].clientX; }, { passive: true });
    bind(root, 'touchend', event => {
      const distance = event.changedTouches[0].clientX - startX;
      if (Math.abs(distance) > 45) show(distance > 0 ? -1 : 1);
      start();
    }, { passive: true });
    bind(root, 'mouseenter', stop);
    bind(root, 'mouseleave', start);
    bind(root, 'focusin', stop);
    bind(root, 'focusout', () => queueMicrotask(start));
    bind(motionPreference, 'change', () => { reduceMotion = motionPreference.matches; start(); });
    if (items.length < 2) root.querySelectorAll('[data-announcement-prev], [data-announcement-next], .chandi-notibar__dots').forEach(item => { item.hidden = true; });
    if (!items.length) root.hidden = true;
    expose();
    syncHeight();
    start();
    registerCleanup(root, () => {
      controller.abort();
      observer.disconnect();
      stop();
      root.getAnimations().forEach(animation => animation.cancel());
      items.forEach(item => item.getAnimations().forEach(animation => animation.cancel()));
      document.documentElement.style.setProperty('--chandi-announcement-height', '0px');
      delete root.dataset.chandiReady;
    });
  };

  const initHeader = root => {
    if (root.dataset.chandiReady === 'true') return;
    root.dataset.chandiReady = 'true';
    const controller = new AbortController();
    const bind = (node, type, callback) => node?.addEventListener(type, callback, { signal: controller.signal });
    const drawer = root.querySelector('[data-menu-drawer]');
    const overlay = root.querySelector('[data-menu-overlay]');
    const opener = root.querySelector('[data-menu-open]');
    const search = root.querySelector('[data-search-panel]');
    const searchInput = root.querySelector('[data-search-input]');
    let searchTrigger;
    const syncHeight = () => document.documentElement.style.setProperty('--chandi-header-height', `${root.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(syncHeight);
    observer.observe(root.querySelector('header') || root);
    syncHeight();
    const setSearch = (open, trigger) => {
      if (!search) return;
      if (open) searchTrigger = trigger || searchTrigger;
      search.hidden = !open;
      root.querySelectorAll('[data-search-toggle]').forEach(button => button.setAttribute('aria-expanded', String(open)));
      if (open) searchInput?.focus();
      else if (search.contains(document.activeElement)) searchTrigger?.focus();
    };
    const setMenu = open => {
      if (open) setSearch(false);
      drawer?.classList.toggle('is-open', open);
      overlay?.classList.toggle('is-open', open);
      if (overlay) overlay.hidden = !open;
      if (drawer) drawer.inert = !open;
      drawer?.setAttribute('aria-hidden', String(!open));
      opener?.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('chandi-locked', open);
      if (open && drawer) trapFocus(drawer, drawer.querySelector('button, a'));
      else removeTrapFocus(opener);
    };
    if (drawer) drawer.inert = true;
    bind(opener, 'click', () => setMenu(true));
    bind(overlay, 'click', () => setMenu(false));
    bind(root.querySelector('[data-menu-close]'), 'click', () => setMenu(false));
    root.querySelectorAll('[data-search-toggle]').forEach(button => bind(button, 'click', () => setSearch(search?.hidden, button)));
    bind(root.querySelector('[data-search-close]'), 'click', () => setSearch(false));
    bind(root.querySelector('#cart-icon-bubble-mobile'), 'click', event => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
      event.preventDefault();
      cartDrawer.open(event.currentTarget);
    });
    bind(document, 'keydown', event => {
      if (event.key !== 'Escape') return;
      if (drawer?.classList.contains('is-open')) setMenu(false);
      if (search && !search.hidden) setSearch(false);
    });
    const countObserver = new MutationObserver(syncMobileCartCount);
    const desktopCart = root.querySelector('#cart-icon-bubble');
    if (desktopCart) countObserver.observe(desktopCart, { childList: true, subtree: true, characterData: true });
    syncMobileCartCount();
    registerCleanup(root, () => {
      controller.abort();
      observer.disconnect();
      countObserver.disconnect();
      if (drawer?.classList.contains('is-open')) { removeTrapFocus(); document.body.classList.remove('chandi-locked'); }
      document.documentElement.style.setProperty('--chandi-header-height', '0px');
      delete root.dataset.chandiReady;
    });
  };

  const syncMobileCartCount = () => {
    const desktopCount = document.querySelector('#cart-icon-bubble em');
    const mobileCart = document.querySelector('#cart-icon-bubble-mobile');
    if (!desktopCount || !mobileCart) return;
    const count = Number.parseInt(desktopCount.textContent, 10) || 0;
    let bubble = mobileCart.querySelector('i');
    if (!count) {
      bubble?.remove();
    } else {
      if (!bubble) {
        bubble = document.createElement('i');
        mobileCart.appendChild(bubble);
      }
      bubble.textContent = String(count);
    }
  };

  /* ---------- CHANDI features: Amsterdam time helpers ---------- */
  const TZ = 'Europe/Amsterdam';
  const DAY_NAMES = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
  const pad = value => String(value).padStart(2, '0');
  const amsParts = (date = new Date()) => {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, hourCycle: 'h23', weekday: 'short',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
    }).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
    const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(parts.weekday) + 1;
    return { y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour, min: +parts.minute, s: +parts.second, weekday };
  };
  const amsToUtc = (y, m, d, h = 0, min = 0) => {
    const guess = Date.UTC(y, m - 1, d, h, min);
    const p = amsParts(new Date(guess));
    const offset = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min) - guess;
    return new Date(guess - offset);
  };
  const readHours = el => ({
    days: (el.dataset.days || '1,2,3,4,5,6').split(',').map(n => parseInt(n, 10)).filter(n => n >= 1 && n <= 7),
    open: Number.isFinite(Number(el.dataset.open)) && el.dataset.open !== '' ? Number(el.dataset.open) : 9,
    close: Number.isFinite(Number(el.dataset.close)) && el.dataset.close !== '' ? Number(el.dataset.close) : 17
  });
  const onVariantChange = (root, callback) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
        const variant = event?.data?.variant;
        const owner = root.closest('product-info');
        if (!root.isConnected || !owner || String(event?.data?.sectionId) !== String(owner.dataset.originalSection || owner.dataset.section)) return;
        callback(variant ? String(variant.id) : null, variant, event?.data?.html);
      });
      registerCleanup(root, unsubscribe);
    }
  };
  const currentVariantId = root => root.closest('product-info, .product')?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;

  /* Live "Nu open / Gesloten" */
  const initStatus = el => {
    if (el.dataset.chandiReady === 'true') return;
    el.dataset.chandiReady = 'true';
    const { days, open, close } = readHours(el);
    const closedDates = (el.dataset.closedDates || '').split(',').map(value => value.trim());
    const dateKey = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
    const label = el.querySelector('[data-chandi-status-text]');
    const update = () => {
      const now = amsParts();
      const minutesNow = now.h * 60 + now.min;
      const closedToday = closedDates.includes(dateKey(now.y, now.m, now.d));
      const isOpen = !closedToday && days.includes(now.weekday) && minutesNow >= open * 60 && minutesNow < close * 60;
      el.classList.toggle('is-open', isOpen);
      if (isOpen) {
        const left = close * 60 - minutesNow;
        label.textContent = left <= 60 ? `Nu open · sluit over ${left} min` : `Nu open · tot ${pad(close)}:00`;
        return;
      }
      if (!closedToday && days.includes(now.weekday) && minutesNow < open * 60) {
        label.textContent = `Gesloten · opent vandaag om ${pad(open)}:00`;
        return;
      }
      for (let i = 1; i <= 366; i++) {
        const weekday = ((now.weekday - 1 + i) % 7) + 1;
        const next = new Date(Date.UTC(now.y, now.m - 1, now.d + i));
        if (days.includes(weekday) && !closedDates.includes(dateKey(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()))) {
          label.textContent = `Gesloten · opent ${i === 1 ? 'morgen' : i <= 6 ? DAY_NAMES[weekday - 1] : `${next.getUTCDate()}-${next.getUTCMonth() + 1}`} om ${pad(open)}:00`;
          return;
        }
      }
    };
    update();
    const timer = setInterval(update, 60000);
    registerCleanup(el, () => { clearInterval(timer); delete el.dataset.chandiReady; });
  };

  /* Drop countdown */
  const initCountdown = root => {
    if (root.dataset.chandiReady === 'true') return;
    root.dataset.chandiReady = 'true';
    const peek = root.querySelector('[data-chandi-peek]');
    peek?.addEventListener('click', () => {
      const media = root.querySelector('[data-chandi-countdown-media]');
      if (!media) return;
      media.classList.add('is-revealed');
      peek.hidden = true;
      media.tabIndex = -1;
      media.focus({ preventScroll: true });
    });
    if (root.dataset.weekly === 'true') {
      const [wh, wm] = (root.dataset.weeklyTime || '00:00').split(':').map(Number);
      const dropH = wh >= 0 && wh <= 23 ? wh : 0;
      const dropM = wm >= 0 && wm <= 59 ? wm : 0;
      const upcoming = root.querySelector('[data-chandi-countdown-upcoming]');
      const liveBox = root.querySelector('[data-chandi-countdown-live]');
      const media = root.querySelector('[data-chandi-countdown-media]');
      const hasTeaser = !!media?.classList.contains('is-teaser');
      const units = {};
      root.querySelectorAll('[data-unit]').forEach(node => { units[node.dataset.unit] = node; });
      const dateLine = root.querySelector('[data-chandi-countdown-date]');
      let shownTarget = 0;
      let isLive;
      const setLive = live => {
        if (live === isLive) return;
        isLive = live;
        upcoming.hidden = live;
        liveBox.hidden = !live;
        media?.classList.toggle('is-teaser', hasTeaser && !live);
        root.classList.toggle('is-live', live);
      };
      const tick = () => {
        const now = amsParts();
        const target = amsToUtc(now.y, now.m, now.d + ((7 - now.weekday) % 7), dropH, dropM).getTime();
        const diff = target - Date.now();
        const live = now.weekday === 7 && diff <= 0;
        setLive(live);
        if (live) return;
        if (dateLine && shownTarget !== target) {
          shownTarget = target;
          const formatted = new Intl.DateTimeFormat('nl-NL', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(target));
          dateLine.textContent = `Live op ${formatted} om ${pad(dropH)}:${pad(dropM)}`;
        }
        const secs = Math.floor(diff / 1000);
        const values = { days: Math.floor(secs / 86400), hours: Math.floor(secs / 3600) % 24, minutes: Math.floor(secs / 60) % 60, seconds: secs % 60 };
        Object.entries(values).forEach(([unit, value]) => {
          const node = units[unit];
          const text = pad(value);
          if (node && node.textContent !== text) node.textContent = text;
        });
      };
      tick();
      const weeklyTimer = setInterval(tick, 1000);
      registerCleanup(root, () => { clearInterval(weeklyTimer); delete root.dataset.chandiReady; });
      return;
    }
    const match = (root.dataset.drop || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    const values = match?.slice(1).map(Number);
    const validDate = values && values[1] >= 1 && values[1] <= 12 && values[2] >= 1 && values[2] <= new Date(Date.UTC(values[0], values[1], 0)).getUTCDate() && values[3] <= 23 && values[4] <= 59;
    if (!validDate) {
      root.querySelector('[data-chandi-countdown-upcoming]').hidden = true;
      root.querySelector('[data-chandi-countdown-live]').hidden = false;
      root.querySelector('[data-chandi-countdown-media]')?.classList.remove('is-teaser');
      root.classList.add('is-live');
      return;
    }
    const [, y, m, d, h, min] = match.map(Number);
    const target = amsToUtc(y, m, d, h, min).getTime();
    const units = {};
    root.querySelectorAll('[data-unit]').forEach(node => { units[node.dataset.unit] = node; });
    const dateLine = root.querySelector('[data-chandi-countdown-date]');
    if (dateLine) {
      const formatted = new Intl.DateTimeFormat('nl-NL', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(target));
      dateLine.textContent = `Live op ${formatted} om ${pad(h)}:${pad(min)}`;
    }
    let timer;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        clearInterval(timer);
        root.querySelector('[data-chandi-countdown-upcoming]').hidden = true;
        root.querySelector('[data-chandi-countdown-live]').hidden = false;
        root.querySelector('[data-chandi-countdown-media]')?.classList.remove('is-teaser');
        root.classList.add('is-live');
        return;
      }
      const s = Math.floor(diff / 1000);
      const values = { days: Math.floor(s / 86400), hours: Math.floor(s / 3600) % 24, minutes: Math.floor(s / 60) % 60, seconds: s % 60 };
      Object.entries(values).forEach(([unit, value]) => {
        const node = units[unit];
        const text = pad(value);
        if (node && node.textContent !== text) node.textContent = text;
      });
    };
    tick();
    if (target > Date.now()) timer = setInterval(tick, 1000);
    registerCleanup(root, () => { clearInterval(timer); delete root.dataset.chandiReady; });
  };

  /* Live stock per size on the product page */
  const initStock = root => {
    if (root.dataset.chandiReady === 'true') return;
    root.dataset.chandiReady = 'true';
    let data = {};
    try { data = JSON.parse(root.querySelector('[data-chandi-stock-data]').textContent); } catch (_) { return; }
    const threshold = parseInt(root.dataset.threshold, 10) || 3;
    const single = root.dataset.single === 'true';
    const text = root.querySelector('[data-chandi-stock-text]');
    const refreshCurrent = container => {
      try {
        const current = JSON.parse(container.querySelector('[data-chandi-stock-current]')?.textContent || '{}');
        if (current.id) data[String(current.id)] = current;
      } catch (_) {}
    };
    refreshCurrent(root);
    const render = (id, variant, html) => {
      if (html) {
        const owner = root.closest('product-info');
        const source = html.getElementById(owner.id) || html.querySelector('product-info');
        if (source) refreshCurrent(source);
      }
      const v = data[id];
      if (!v) {
        root.classList.remove('is-low');
        root.classList.add('is-out');
        text.textContent = 'Deze combinatie is niet beschikbaar';
        return;
      }
      const size = single ? '' : ` in ${v.title}`;
      root.classList.remove('is-low', 'is-out');
      if (!v.available) {
        root.classList.add('is-out');
        text.textContent = `Uitverkocht${single ? '' : ' in deze maat'}`;
      } else if (v.qty !== null && v.qty <= threshold) {
        root.classList.add('is-low');
        text.textContent = v.qty === 1 ? `Laatste stuk${size}` : `Nog maar ${v.qty} op voorraad${size}`;
      } else {
        text.textContent = 'Op voorraad';
      }
    };
    const initial = currentVariantId(root);
    if (initial) render(initial);
    onVariantChange(root, render);
    const controller = new AbortController();
    if (typeof subscribe === 'function') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
        const owner = root.closest('product-info');
        const id = currentVariantId(root);
        if (!root.isConnected || !owner || !id) return;
        try {
          const url = new URL(owner.dataset.url, window.location.origin);
          url.searchParams.set('section_id', owner.dataset.section);
          url.searchParams.set('variant', id);
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (root.isConnected && currentVariantId(root) === id) render(id, null, html);
        } catch (_) {}
      });
      registerCleanup(root, unsubscribe);
    }
    registerCleanup(root, () => controller.abort());
  };

  const initService = root => {
    if (root.dataset.chandiReady === 'true') return;
    root.dataset.chandiReady = 'true';
    const single = root.dataset.single === 'true';
    const selectedId = currentVariantId(root);
    const stockData = root.closest('product-info, .product')?.querySelector('[data-chandi-stock-data]');
    let variantTitle = root.dataset.variantTitle || '';
    try { variantTitle = JSON.parse(stockData?.textContent || '{}')[selectedId]?.title || variantTitle; } catch (_) {}
    onVariantChange(root, (id, variant) => { variantTitle = variant?.title || ''; });
    const whatsapp = root.querySelector('[data-chandi-whatsapp]');
    const controller = new AbortController();
    whatsapp?.addEventListener('click', () => {
      const size = !single && variantTitle ? ` (maat ${variantTitle})` : '';
      const message = `Hoi CHANDI! Ik heb een vraag over ${root.dataset.productTitle}${size}. ${root.dataset.productLink}`;
      whatsapp.href = `https://wa.me/${whatsapp.dataset.number}?text=${encodeURIComponent(message)}`;
    }, { signal: controller.signal });
    registerCleanup(root, () => { controller.abort(); delete root.dataset.chandiReady; });
  };

  const init = scope => {
    scope.querySelectorAll('[data-chandi-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-chandi-benefits]').forEach(initBenefits);
    scope.querySelectorAll('[data-chandi-announcement]').forEach(initAnnouncement);
    scope.querySelectorAll('[data-chandi-header]').forEach(initHeader);
    scope.querySelectorAll('[data-chandi-status]').forEach(initStatus);
    scope.querySelectorAll('[data-chandi-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-chandi-stock]').forEach(initStock);
    scope.querySelectorAll('[data-chandi-service]').forEach(initService);
  };

  const syncMedia = scope => {
    scope.querySelectorAll('.chandi-home video[autoplay]').forEach(video => {
      video.controls = true;
      if (reduceMotion) video.pause();
    });
  };
  motionPreference.addEventListener('change', () => { reduceMotion = motionPreference.matches; syncMedia(document); });
  const initialize = scope => { init(scope); syncMedia(scope); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => { cleanup(document.createElement('div')); initialize(event.target); });
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
  document.addEventListener('chandi:product-rendered', event => {
    const oldNode = event.target.nextElementSibling;
    if (oldNode?.style.display === 'none') cleanup(oldNode);
    cleanup(document.createElement('div'));
    initialize(event.target.closest('main') || event.target);
  });
})();
